import React, { useEffect, useRef, useState } from 'react';
import { IoCloseOutline, IoCodeOutline, IoPlay } from 'react-icons/io5';
import { AiOutlineDelete } from 'react-icons/ai';
import { BsMic } from 'react-icons/bs';
import { HiStop } from 'react-icons/hi2';
import { LuChevronDown, LuExpand, LuCheck } from 'react-icons/lu';
import CenterModal from '../molecules/Modal/CenterModal';
import Button from '../molecules/Button';
import userService from '../../services/userService';
import offerService from '../../services/offerService';
import {
  PropertiesPanel,
  PropertiesHeader,
  PropertiesBody,
  PropertiesFooter,
  FormGroup,
  FormTextarea,
  InsertVariableWrapper,
  InsertVariableButton,
  VariablesDropdown,
  VariableItem,
  CharCounter,
  CheckboxRow,
  DeleteConfirmTooltip,
  ConfirmBtn,
  ActionIconWrapper,
  NodeTypeRow,
  NodeTypeLabel,
  MessageHeaderRow,
  HeaderLabel,
  AltMessageHint,
  DelayRow,
  DelayInput,
  DelayUnitSelect,
  VoiceRecordedContainer,
  VoiceRecordedRow,
  VoicePlayButton,
  HiddenAudio,
  VoiceMeta,
  VoiceWaveTrack,
  VoiceWaveFill,
  VoiceFilename,
  VoiceRemoveButton,
  RecordingContainer,
  RecordingPulseOuter,
  RecordingPulseInner,
  RecordingText,
  RecordingTime,
  RecordingSubtext,
  RecordingActions,
  RecordingCancelButton,
  RecordingStopButton,
  StartRecordButton,
  StartRecordIcon,
  StartRecordText,
  StartRecordTitle,
  StartRecordSubtitle,
  FormInput,
  EmailAccountSelector,
  EmailAccountTrigger,
  EmailAccountDropdown,
  EmailAccountOption,
  EmailBodyWrapper,
  EmailBodyExpandBtn,
  ExpandedEditorBody,
  TinyMceModalGlobalStyle,
} from './FlowBuilder.styles';
import SmallEditor from '../atoms/CreateCompaign/SmallEditor';
import AutopilotToggleRow from './AutopilotToggleRow';
import { CreateOfferHint } from './FlowBuilderActionsPanel';

const EMAIL_VARIABLES = [
  { label: 'First Name', value: '{{first_name}}' },
  { label: 'Last Name', value: '{{last_name}}' },
  { label: 'Company Name', value: '{{company_name}}' },
  { label: 'Job Title', value: '{{job_title}}' },
  { label: 'Location', value: '{{location}}' },
];

const SendEmailSection = ({ selectedNode, updateNodeData, emailAccounts, hasPriorMessageNode }) => {
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [subjectVarsOpen, setSubjectVarsOpen] = useState(false);
  const [bodyExpanded, setBodyExpanded] = useState(false);
  const subjectInputRef = useRef(null);

  const selectedIds = selectedNode.data.emailAccountIds || [];
  const selectedAccounts = emailAccounts.filter(a => selectedIds.includes(a.id));

  const toggleAccount = (account) => {
    const already = selectedIds.includes(account.id);
    const newIds = already
      ? selectedIds.filter(id => id !== account.id)
      : [...selectedIds, account.id];
    const newEmails = emailAccounts
      .filter(a => newIds.includes(a.id))
      .map(a => a.email);
    updateNodeData(selectedNode.id, 'emailAccountIds', newIds);
    updateNodeData(selectedNode.id, 'emailAddresses', newEmails);
  };

  const triggerLabel = () => {
    if (selectedAccounts.length === 0) return 'Select email account...';
    if (selectedAccounts.length === 1) return selectedAccounts[0].email;
    return `${selectedAccounts.length} accounts selected`;
  };

  const insertSubjectVariable = (variable) => {
    const input = subjectInputRef.current;
    if (!input) return;
    const start = input.selectionStart;
    const end = input.selectionEnd;
    const current = selectedNode.data.emailSubject || '';
    const newValue = current.substring(0, start) + variable + current.substring(end);
    updateNodeData(selectedNode.id, 'emailSubject', newValue);
    setSubjectVarsOpen(false);
    setTimeout(() => {
      input.focus();
      const pos = start + variable.length;
      input.setSelectionRange(pos, pos);
    }, 0);
  };

  return (
    <>
      <FormGroup>
        <label>From Account</label>
        <EmailAccountSelector>
          <EmailAccountTrigger
            type="button"
            $hasValue={selectedAccounts.length > 0}
            $open={accountDropdownOpen}
            onClick={() => setAccountDropdownOpen(prev => !prev)}
          >
            <span>{triggerLabel()}</span>
            <LuChevronDown />
          </EmailAccountTrigger>
          {accountDropdownOpen && (
            <EmailAccountDropdown>
              {emailAccounts.length === 0 && (
                <EmailAccountOption as="div" $static $muted>
                  <div className="account-info">
                    <span className="account-label">No email accounts found</span>
                  </div>
                </EmailAccountOption>
              )}
              {emailAccounts.map(account => {
                const isSelected = selectedIds.includes(account.id);
                return (
                  <EmailAccountOption
                    key={account.id}
                    type="button"
                    $selected={isSelected}
                    onClick={() => toggleAccount(account)}
                  >
                    <div className="account-info">
                      <span className="account-label">{account.email}</span>
                    </div>
                    <span className="check-icon">
                      <LuCheck size={14} />
                    </span>
                  </EmailAccountOption>
                );
              })}
            </EmailAccountDropdown>
          )}
        </EmailAccountSelector>
      </FormGroup>

      <FormGroup>
        <MessageHeaderRow>
          <HeaderLabel>Subject</HeaderLabel>
          <InsertVariableWrapper>
            <InsertVariableButton
              type="button"
              onClick={() => setSubjectVarsOpen(prev => !prev)}
            >
              <IoCodeOutline />
              Insert Variable
            </InsertVariableButton>
            {subjectVarsOpen && (
              <VariablesDropdown>
                {EMAIL_VARIABLES.map(v => (
                  <VariableItem key={v.value} type="button" onClick={() => insertSubjectVariable(v.value)}>
                    <span className="label">{v.label}</span>
                    <span className="value">{v.value}</span>
                  </VariableItem>
                ))}
              </VariablesDropdown>
            )}
          </InsertVariableWrapper>
        </MessageHeaderRow>
        <FormInput
          ref={subjectInputRef}
          type="text"
          placeholder="Enter email subject..."
          value={selectedNode.data.emailSubject || ''}
          onChange={e => updateNodeData(selectedNode.id, 'emailSubject', e.target.value)}
          onClick={() => setSubjectVarsOpen(false)}
        />
      </FormGroup>

      <FormGroup>
        <label>Email Body</label>
        <EmailBodyWrapper>
          <SmallEditor
            withVariables
            placeholder="Write your email body here..."
            value={selectedNode.data.emailBody || ''}
            onChange={e => updateNodeData(selectedNode.id, 'emailBody', e.target.value)}
          />
          <EmailBodyExpandBtn
            type="button"
            title="Expand editor"
            onClick={() => setBodyExpanded(true)}
          >
            <LuExpand />
          </EmailBodyExpandBtn>
        </EmailBodyWrapper>
      </FormGroup>

      <TinyMceModalGlobalStyle />
      <CenterModal
        title="Email Body"
        open={bodyExpanded}
        setOpen={setBodyExpanded}
        width="760"
      >
        <ExpandedEditorBody>
          <SmallEditor
            withVariables
            placeholder="Write your email body here..."
            value={selectedNode.data.emailBody || ''}
            onChange={e => updateNodeData(selectedNode.id, 'emailBody', e.target.value)}
          />
        </ExpandedEditorBody>
      </CenterModal>

      <FormGroup>
        <CheckboxRow $dim={!hasPriorMessageNode}>
          <input
            type="checkbox"
            disabled={!hasPriorMessageNode}
            checked={!!selectedNode.data.skipIfReplied}
            onChange={e => updateNodeData(selectedNode.id, 'skipIfReplied', e.target.checked)}
          />
          <span>Skip if lead already replied</span>
        </CheckboxRow>
        {!hasPriorMessageNode && (
          <AltMessageHint>Add a prior email or message step to enable this option.</AltMessageHint>
        )}
        {hasPriorMessageNode && (
          <AltMessageHint>Skips when the lead has replied by email or LinkedIn.</AltMessageHint>
        )}
      </FormGroup>
    </>
  );
};

const getOfferLabel = offer =>
  offer?.offerName || offer?.task_name || offer?.promoting || offer?.user_company || `Offer #${offer?.id}`;

const getLinkedInProfileLabel = profile =>
  `${profile?.firstName || ''} ${profile?.lastName || ''}`.trim() ||
  profile?.linkedinUrl ||
  `Profile #${profile?.id}`;

const SendAiEmailFromAccountSection = ({ selectedNode, updateNodeData, emailAccounts }) => {
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const selectedIds = selectedNode.data.emailAccountIds || [];
  const selectedAccounts = emailAccounts.filter(a => selectedIds.includes(a.id));

  const toggleAccount = account => {
    const already = selectedIds.includes(account.id);
    const newIds = already
      ? selectedIds.filter(id => id !== account.id)
      : [...selectedIds, account.id];
    const newEmails = emailAccounts.filter(a => newIds.includes(a.id)).map(a => a.email);
    updateNodeData(selectedNode.id, 'emailAccountIds', newIds);
    updateNodeData(selectedNode.id, 'emailAddresses', newEmails);
  };

  const triggerLabel = () => {
    if (selectedAccounts.length === 0) return 'Select email account...';
    if (selectedAccounts.length === 1) return selectedAccounts[0].email;
    return `${selectedAccounts.length} accounts selected`;
  };

  return (
    <FormGroup>
      <label>From Account</label>
      <EmailAccountSelector>
        <EmailAccountTrigger
          type="button"
          $hasValue={selectedAccounts.length > 0}
          $open={accountDropdownOpen}
          onClick={() => setAccountDropdownOpen(prev => !prev)}>
          <span>{triggerLabel()}</span>
          <LuChevronDown />
        </EmailAccountTrigger>
        {accountDropdownOpen && (
          <EmailAccountDropdown>
            {emailAccounts.length === 0 && (
              <EmailAccountOption as="div" $static $muted>
                <div className="account-info">
                  <span className="account-label">No email accounts found</span>
                </div>
              </EmailAccountOption>
            )}
            {emailAccounts.map(account => {
              const isSelected = selectedIds.includes(account.id);
              return (
                <EmailAccountOption
                  key={account.id}
                  type="button"
                  $selected={isSelected}
                  onClick={() => toggleAccount(account)}>
                  <div className="account-info">
                    <span className="account-label">{account.email}</span>
                  </div>
                  <span className="check-icon">
                    <LuCheck size={14} />
                  </span>
                </EmailAccountOption>
              );
            })}
          </EmailAccountDropdown>
        )}
      </EmailAccountSelector>
    </FormGroup>
  );
};

const SendAiMessageOfferSection = ({ selectedNode, updateNodeData, emailAccounts }) => {
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [offersError, setOffersError] = useState('');
  const [offerDropdownOpen, setOfferDropdownOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadOffers = async () => {
      setOffersLoading(true);
      setOffersError('');
      try {
        const res = await offerService.getAllOffers({ getAll: true, itemsPerPage: 100 });
        if (!mounted) return;
        setOffers(res?.items || []);
      } catch (e) {
        if (mounted) setOffersError(e?.message || 'Failed to load offers.');
      } finally {
        if (mounted) setOffersLoading(false);
      }
    };

    if (selectedNode?.id) loadOffers();
    setOfferDropdownOpen(false);

    return () => {
      mounted = false;
    };
  }, [selectedNode?.id]);

  const offerList = Array.isArray(offers) ? offers : [];
  const selectedId = selectedNode?.data?.offerId;
  const selectedOffer = offerList.find(o => String(o.id) === String(selectedId));

  const triggerLabel = () => {
    if (offersLoading) return 'Loading offers...';
    if (selectedOffer) return getOfferLabel(selectedOffer);
    if (selectedNode?.data?.offerName) return selectedNode.data.offerName;
    return 'Select an offer...';
  };

  const handleSelectOffer = offer => {
    updateNodeData(selectedNode.id, 'offerId', offer.id);
    updateNodeData(selectedNode.id, 'offerName', getOfferLabel(offer));
    setOfferDropdownOpen(false);
  };

  const autopilotOn = selectedNode?.data?.autopilot !== false;

  return (
    <FormGroup>
      <label>Offer</label>
      <EmailAccountSelector>
        <EmailAccountTrigger
          type="button"
          $hasValue={!!selectedId || !!selectedNode?.data?.offerName}
          $open={offerDropdownOpen}
          disabled={offersLoading}
          onClick={() => setOfferDropdownOpen(prev => !prev)}>
          <span>{triggerLabel()}</span>
          <LuChevronDown />
        </EmailAccountTrigger>

        {offerDropdownOpen && (
          <EmailAccountDropdown>
            {offersError && (
              <EmailAccountOption as="div" $static>
                <div className="account-info">
                  <span className="account-label">{offersError}</span>
                </div>
              </EmailAccountOption>
            )}

            {!offersError && offerList.length === 0 && !offersLoading && (
              <EmailAccountOption as="div" $static $muted>
                <div className="account-info">
                  <span className="account-label">No offers found</span>
                </div>
              </EmailAccountOption>
            )}

            {offerList.map(offer => {
              const isSelected = String(selectedId) === String(offer.id);
              return (
                <EmailAccountOption
                  key={offer.id}
                  type="button"
                  $selected={isSelected}
                  onClick={() => handleSelectOffer(offer)}>
                  <div className="account-info">
                    <span className="account-label">{getOfferLabel(offer)}</span>
                    {offer.user_company && (
                      <span className="account-email">{offer.user_company}</span>
                    )}
                  </div>
                  <span className="check-icon">
                    <LuCheck size={14} />
                  </span>
                </EmailAccountOption>
              );
            })}
          </EmailAccountDropdown>
        )}
      </EmailAccountSelector>
      <CreateOfferHint />
      <SendAiEmailFromAccountSection
        selectedNode={selectedNode}
        updateNodeData={updateNodeData}
        emailAccounts={emailAccounts}
      />
      <AutopilotToggleRow
        value={autopilotOn}
        onChange={checked => updateNodeData(selectedNode.id, 'autopilot', checked)}
      />
    </FormGroup>
  );
};

const SendAiLinkedInFromProfileSection = ({ selectedNode, updateNodeData, linkedinProfiles }) => {
  const [profileDropdownOpen, setProfileDropdownOpen] = useState(false);
  const selectedIds = selectedNode.data.linkedinProfileIds || [];
  const selectedProfiles = linkedinProfiles.filter(p => selectedIds.includes(p.id));

  const toggleProfile = profile => {
    const already = selectedIds.includes(profile.id);
    const newIds = already
      ? selectedIds.filter(id => id !== profile.id)
      : [...selectedIds, profile.id];
    const newNames = linkedinProfiles
      .filter(p => newIds.includes(p.id))
      .map(getLinkedInProfileLabel);
    updateNodeData(selectedNode.id, 'linkedinProfileIds', newIds);
    updateNodeData(selectedNode.id, 'linkedinProfileNames', newNames);
  };

  const triggerLabel = () => {
    if (selectedProfiles.length === 0) return 'Select LinkedIn profile...';
    if (selectedProfiles.length === 1) return getLinkedInProfileLabel(selectedProfiles[0]);
    return `${selectedProfiles.length} profiles selected`;
  };

  return (
    <FormGroup>
      <label>LinkedIn Profile</label>
      <EmailAccountSelector>
        <EmailAccountTrigger
          type="button"
          $hasValue={selectedProfiles.length > 0}
          $open={profileDropdownOpen}
          onClick={() => setProfileDropdownOpen(prev => !prev)}>
          <span>{triggerLabel()}</span>
          <LuChevronDown />
        </EmailAccountTrigger>
        {profileDropdownOpen && (
          <EmailAccountDropdown>
            {linkedinProfiles.length === 0 && (
              <EmailAccountOption as="div" $static $muted>
                <div className="account-info">
                  <span className="account-label">No LinkedIn profiles found</span>
                </div>
              </EmailAccountOption>
            )}
            {linkedinProfiles.map(profile => {
              const isSelected = selectedIds.includes(profile.id);
              return (
                <EmailAccountOption
                  key={profile.id}
                  type="button"
                  $selected={isSelected}
                  onClick={() => toggleProfile(profile)}>
                  <div className="account-info">
                    <span className="account-label">{getLinkedInProfileLabel(profile)}</span>
                  </div>
                  <span className="check-icon">
                    <LuCheck size={14} />
                  </span>
                </EmailAccountOption>
              );
            })}
          </EmailAccountDropdown>
        )}
      </EmailAccountSelector>
    </FormGroup>
  );
};

const SendAiLinkedInMessageSection = ({ selectedNode, updateNodeData, linkedinProfiles }) => {
  const autopilotOn = selectedNode?.data?.autopilot !== false;
  const [offers, setOffers] = useState([]);
  const [offersLoading, setOffersLoading] = useState(false);
  const [offersError, setOffersError] = useState('');
  const [offerDropdownOpen, setOfferDropdownOpen] = useState(false);

  useEffect(() => {
    let mounted = true;

    const loadOffers = async () => {
      setOffersLoading(true);
      setOffersError('');
      try {
        const res = await offerService.getAllOffers({ getAll: true, itemsPerPage: 100 });
        if (!mounted) return;
        setOffers(res?.items || []);
      } catch (e) {
        if (mounted) setOffersError(e?.message || 'Failed to load offers.');
      } finally {
        if (mounted) setOffersLoading(false);
      }
    };

    if (selectedNode?.id) loadOffers();
    setOfferDropdownOpen(false);

    return () => {
      mounted = false;
    };
  }, [selectedNode?.id]);

  const offerList = Array.isArray(offers) ? offers : [];
  const selectedId = selectedNode?.data?.offerId;
  const selectedOffer = offerList.find(o => String(o.id) === String(selectedId));

  const triggerLabel = () => {
    if (offersLoading) return 'Loading offers...';
    if (selectedOffer) return getOfferLabel(selectedOffer);
    if (selectedNode?.data?.offerName) return selectedNode.data.offerName;
    return 'Select an offer...';
  };

  const handleSelectOffer = offer => {
    updateNodeData(selectedNode.id, 'offerId', offer.id);
    updateNodeData(selectedNode.id, 'offerName', getOfferLabel(offer));
    setOfferDropdownOpen(false);
  };

  return (
    <FormGroup>
      <label>Offer</label>
      <EmailAccountSelector>
        <EmailAccountTrigger
          type="button"
          $hasValue={!!selectedId || !!selectedNode?.data?.offerName}
          $open={offerDropdownOpen}
          disabled={offersLoading}
          onClick={() => setOfferDropdownOpen(prev => !prev)}>
          <span>{triggerLabel()}</span>
          <LuChevronDown />
        </EmailAccountTrigger>

        {offerDropdownOpen && (
          <EmailAccountDropdown>
            {offersError && (
              <EmailAccountOption as="div" $static>
                <div className="account-info">
                  <span className="account-label">{offersError}</span>
                </div>
              </EmailAccountOption>
            )}

            {!offersError && offerList.length === 0 && !offersLoading && (
              <EmailAccountOption as="div" $static $muted>
                <div className="account-info">
                  <span className="account-label">No offers found</span>
                </div>
              </EmailAccountOption>
            )}

            {offerList.map(offer => {
              const isSelected = String(selectedId) === String(offer.id);
              return (
                <EmailAccountOption
                  key={offer.id}
                  type="button"
                  $selected={isSelected}
                  onClick={() => handleSelectOffer(offer)}>
                  <div className="account-info">
                    <span className="account-label">{getOfferLabel(offer)}</span>
                    {offer.user_company && (
                      <span className="account-email">{offer.user_company}</span>
                    )}
                  </div>
                  <span className="check-icon">
                    <LuCheck size={14} />
                  </span>
                </EmailAccountOption>
              );
            })}
          </EmailAccountDropdown>
        )}
      </EmailAccountSelector>
      <CreateOfferHint />
      <SendAiLinkedInFromProfileSection
        selectedNode={selectedNode}
        updateNodeData={updateNodeData}
        linkedinProfiles={linkedinProfiles}
      />
      <AutopilotToggleRow
        value={autopilotOn}
        onChange={checked => updateNodeData(selectedNode.id, 'autopilot', checked)}
      />
    </FormGroup>
  );
};

const GoogleCalendarMeetingSection = ({ selectedNode, updateNodeData }) => {
  const [accountDropdownOpen, setAccountDropdownOpen] = useState(false);
  const [accountsLoading, setAccountsLoading] = useState(true);
  const [accountsError, setAccountsError] = useState('');
  const [accounts, setAccounts] = useState([]);

  useEffect(() => {
    let mounted = true;
    const load = async () => {
      setAccountsLoading(true);
      setAccountsError('');
      try {
        const res = await userService.getGoogleCalendarAccounts();
        const rows = Array.isArray(res?.data) ? res.data : [];
        if (!mounted) return;

        setAccounts(rows);

        const selectedId = selectedNode?.data?.googleCalendarAccountId;
        if (!selectedId && rows.length === 1) {
          updateNodeData(selectedNode.id, 'googleCalendarAccountId', rows[0].id);
          updateNodeData(selectedNode.id, 'googleCalendarConnectedEmail', rows[0].connectedEmail || null);
        }
      } catch (e) {
        if (mounted) setAccountsError(e?.message || 'Failed to load Google Calendar accounts.');
      } finally {
        if (mounted) setAccountsLoading(false);
      }
    };

    // Fetch when the node gets selected / changes
    if (selectedNode?.id) load();
    setAccountDropdownOpen(false);
    return () => {
      mounted = false;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedNode?.id]);

  const accountList = Array.isArray(accounts) ? accounts : [];
  const selectedId = selectedNode?.data?.googleCalendarAccountId;
  const selectedAccount = accountList.find(a => a.id === selectedId);

  const triggerLabel = () => {
    if (accountsLoading) return 'Loading accounts...';
    if (selectedAccount?.connectedEmail) return selectedAccount.connectedEmail;
    if (selectedNode?.data?.googleCalendarConnectedEmail) return selectedNode.data.googleCalendarConnectedEmail;
    return 'Select Google account...';
  };

  const handleSelectAccount = account => {
    updateNodeData(selectedNode.id, 'googleCalendarAccountId', account.id);
    updateNodeData(selectedNode.id, 'googleCalendarConnectedEmail', account.connectedEmail || null);
    setAccountDropdownOpen(false);
  };

  return (
    <>
      <FormGroup>
        <label>Google Calendar Account</label>
        <EmailAccountSelector>
          <EmailAccountTrigger
            type="button"
            $hasValue={!!selectedId || !!selectedNode?.data?.googleCalendarConnectedEmail}
            $open={accountDropdownOpen}
            disabled={accountsLoading}
            onClick={() => setAccountDropdownOpen(prev => !prev)}
          >
            <span>{triggerLabel()}</span>
            <LuChevronDown />
          </EmailAccountTrigger>

          {accountDropdownOpen && (
            <EmailAccountDropdown>
              {accountsError && (
                <EmailAccountOption as="div" $static>
                  <div className="account-info">
                    <span className="account-label">{accountsError}</span>
                  </div>
                </EmailAccountOption>
              )}

              {!accountsError && accountList.length === 0 && !accountsLoading && (
                <EmailAccountOption as="div" $static $muted>
                  <div className="account-info">
                    <span className="account-label">No Google Calendar accounts found</span>
                  </div>
                </EmailAccountOption>
              )}

              {accountList.map(account => {
                const isSelected = selectedId === account.id;
                return (
                  <EmailAccountOption
                    key={account.id}
                    type="button"
                    $selected={isSelected}
                    onClick={() => handleSelectAccount(account)}
                  >
                    <div className="account-info">
                      <span className="account-label">
                        {account.connectedEmail || `Google account #${account.id}`}
                      </span>
                    </div>
                    <span className="check-icon">
                      <LuCheck size={14} />
                    </span>
                  </EmailAccountOption>
                );
              })}
            </EmailAccountDropdown>
          )}
        </EmailAccountSelector>
      </FormGroup>
    </>
  );
};

const FlowBuilderPropertiesPanel = ({
  open,
  selectedNode,
  selectedNodeConfig,
  setPropertiesPanelOpen,
  confirmingDelete,
  deleteConfirmMessage,
  deleteConfirmWide,
  onRequestDelete,
  onCancelDelete,
  onConfirmDelete,
  hasPriorMessageNode,
  messageTextareaRef,
  variablesDropdownOpen,
  setVariablesDropdownOpen,
  messageVariables,
  insertVariable,
  messageLimits,
  updateNodeData,
  isRecording,
  recordingDuration,
  recordedAudioUrl,
  audioPlayerRef,
  removeRecording,
  cancelRecording,
  stopRecording,
  startRecording,
  formatRecordingTime,
  emailAccounts = [],
  linkedinProfiles = [],
  onSaveStep,
}) => {
  return (
  <PropertiesPanel $open={open}>
    <PropertiesHeader>
      <h3>{selectedNodeConfig?.label || 'Properties'}</h3>
      <IoCloseOutline
        size={25}
        color="var(--black)"
        onClick={() => {
          setPropertiesPanelOpen(false);
          onCancelDelete();
        }}
      />
    </PropertiesHeader>
    <PropertiesBody>
      {selectedNode && selectedNodeConfig && (
        <>
          <FormGroup>
            <label>Node Type</label>
            <NodeTypeRow>
              <ActionIconWrapper
                $bgColor={selectedNodeConfig.bgColor}
                $color={selectedNodeConfig.color}
              >
                {selectedNodeConfig.icon}
              </ActionIconWrapper>
              <NodeTypeLabel>{selectedNodeConfig.label}</NodeTypeLabel>
            </NodeTypeRow>
          </FormGroup>

          {selectedNode.type === 'send_ai_email' && (
            <SendAiMessageOfferSection
              selectedNode={selectedNode}
              updateNodeData={updateNodeData}
              emailAccounts={emailAccounts}
            />
          )}

          {selectedNode.type === 'send_linkedin_ai_message' && (
            <SendAiLinkedInMessageSection
              selectedNode={selectedNode}
              updateNodeData={updateNodeData}
              linkedinProfiles={linkedinProfiles}
            />
          )}

          {(selectedNode.type === 'send_message' ||
            selectedNode.type === 'send_connection' ||
            selectedNode.type === 'comment_post') && (
              <FormGroup>
                <MessageHeaderRow>
                  <HeaderLabel>
                    {selectedNode.type === 'comment_post' ? 'Comment Content' : 'Message Content'}
                  </HeaderLabel>
                  <InsertVariableWrapper>
                    <InsertVariableButton
                      type="button"
                      onClick={() => setVariablesDropdownOpen(prev => !prev)}
                    >
                      <IoCodeOutline />
                      Insert Variable
                    </InsertVariableButton>
                    {variablesDropdownOpen && (
                      <VariablesDropdown>
                        {messageVariables.map(v => (
                          <VariableItem key={v.value} type="button" onClick={() => insertVariable(v.value)}>
                            <span className="label">{v.label}</span>
                            <span className="value">{v.value}</span>
                          </VariableItem>
                        ))}
                      </VariablesDropdown>
                    )}
                  </InsertVariableWrapper>
                </MessageHeaderRow>
                <FormTextarea
                  ref={messageTextareaRef}
                  placeholder={
                    selectedNode.type === 'comment_post'
                      ? 'Enter your comment...'
                      : 'Enter your message...'
                  }
                  value={selectedNode.data.message || ''}
                  maxLength={messageLimits[selectedNode.type] || undefined}
                  onChange={e => updateNodeData(selectedNode.id, 'message', e.target.value)}
                  onClick={() => setVariablesDropdownOpen(false)}
                />
                {messageLimits[selectedNode.type] && (
                  <CharCounter $over={(selectedNode.data.message || '').length >= messageLimits[selectedNode.type]}>
                    {(selectedNode.data.message || '').length} / {messageLimits[selectedNode.type]}
                  </CharCounter>
                )}
              </FormGroup>
            )}

          {selectedNode.type === 'send_connection' && (
            <FormGroup>
              <CheckboxRow>
                <input
                  type="checkbox"
                  checked={!!selectedNode.data.sendWithoutMessageOnFail}
                  onChange={e =>
                    updateNodeData(selectedNode.id, 'sendWithoutMessageOnFail', e.target.checked)
                  }
                />
                <span>Send connection request without message if failed</span>
              </CheckboxRow>
            </FormGroup>
          )}

          {(selectedNode.type === 'send_message' || selectedNode.type === 'send_voice_message') && (
            <FormGroup>
              <CheckboxRow $dim={!hasPriorMessageNode}>
                <input
                  type="checkbox"
                  disabled={!hasPriorMessageNode}
                  checked={!!selectedNode.data.skipIfReplied}
                  onChange={e => updateNodeData(selectedNode.id, 'skipIfReplied', e.target.checked)}
                />
                <span>Skip if lead already replied</span>
              </CheckboxRow>
            </FormGroup>
          )}

          {(selectedNode.type === 'send_message' ||
            selectedNode.type === 'send_connection' ||
            selectedNode.type === 'comment_post') && (
              <FormGroup>
                <MessageHeaderRow>
                  <HeaderLabel>
                    Alternative Message <AltMessageHint>(if message exceeds limit)</AltMessageHint>
                  </HeaderLabel>
                </MessageHeaderRow>
                <FormTextarea
                  placeholder="Shorter fallback message if the main message is too long after variables are replaced..."
                  value={selectedNode.data.alternativeMessage || ''}
                  maxLength={messageLimits[selectedNode.type] || undefined}
                  onChange={e => updateNodeData(selectedNode.id, 'alternativeMessage', e.target.value)}
                />
                {messageLimits[selectedNode.type] && (
                  <CharCounter $over={(selectedNode.data.alternativeMessage || '').length >= messageLimits[selectedNode.type]}>
                    {(selectedNode.data.alternativeMessage || '').length} / {messageLimits[selectedNode.type]}
                  </CharCounter>
                )}
              </FormGroup>
            )}

          {selectedNode.type === 'delay' && (
            <FormGroup>
              <label>Delay Duration (days)</label>
              <DelayRow>
                <DelayInput
                  type="number"
                  min="1"
                  placeholder="1"
                  value={selectedNode.data.delayValue || selectedNode.data.delay || ''}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    updateNodeData(selectedNode.id, 'delayValue', val);
                    updateNodeData(selectedNode.id, 'delay', val);
                  }}
                />
                <DelayUnitSelect
                  value={selectedNode.data.delayUnit || 'days'}
                  onChange={e => updateNodeData(selectedNode.id, 'delayUnit', e.target.value)}
                >
                  <option value="days">Days</option>
                  <option value="hours">Hours</option>
                  <option value="minutes">Minutes</option>
                </DelayUnitSelect>
              </DelayRow>
            </FormGroup>
          )}

          {selectedNode.type === 'wait_for_connection' && (
            <FormGroup>
              <label>Max Wait Time (days)</label>
              <DelayRow>
                <DelayInput
                  type="number"
                  min="1"
                  placeholder="4"
                  value={selectedNode.data.maxWaitTime || ''}
                  onChange={e => {
                    const val = parseInt(e.target.value, 10);
                    updateNodeData(selectedNode.id, 'maxWaitTime', val);
                  }}
                />
                <DelayUnitSelect
                  value={selectedNode.data.maxWaitUnit || 'days'}
                  onChange={e => updateNodeData(selectedNode.id, 'maxWaitUnit', e.target.value)}
                >
                  <option value="days">Days</option>
                  <option value="hours">Hours</option>
                  <option value="minutes">Minutes</option>
                </DelayUnitSelect>
              </DelayRow>
            </FormGroup>
          )}

          {selectedNode.type === 'send_email' && (
            <SendEmailSection
              selectedNode={selectedNode}
              updateNodeData={updateNodeData}
              emailAccounts={emailAccounts}
              hasPriorMessageNode={hasPriorMessageNode}
            />
          )}

          {selectedNode.type === 'check_google_calendar_meeting' && (
            <GoogleCalendarMeetingSection
              selectedNode={selectedNode}
              updateNodeData={updateNodeData}
            />
          )}

          {selectedNode.type === 'send_voice_message' && (
            <FormGroup>
              <label>Voice Message</label>
              {selectedNode.data.audioFilename && !isRecording ? (
                <VoiceRecordedContainer>
                  <VoiceRecordedRow>
                    <VoicePlayButton
                      type="button"
                      onClick={() => {
                        const player = audioPlayerRef.current;
                        if (!player) return;
                        if (player.paused) player.play();
                        else player.pause();
                      }}
                    >
                      <IoPlay color='var(--white)' />
                    </VoicePlayButton>

                    {recordedAudioUrl && (
                      <HiddenAudio ref={audioPlayerRef} src={recordedAudioUrl} preload="metadata" />
                    )}

                    <VoiceMeta>
                      <VoiceWaveTrack>
                        <VoiceWaveFill />
                      </VoiceWaveTrack>
                      <VoiceFilename>
                        {selectedNode.data.audioFilename}
                      </VoiceFilename>
                    </VoiceMeta>

                    <VoiceRemoveButton
                      type="button"
                      onClick={removeRecording}
                      title="Remove recording"
                    >
                      <AiOutlineDelete />
                    </VoiceRemoveButton>
                  </VoiceRecordedRow>
                </VoiceRecordedContainer>
              ) : isRecording ? (
                <RecordingContainer>
                  <RecordingPulseOuter>
                    <RecordingPulseInner>
                      <BsMic color="var(--white)" />
                    </RecordingPulseInner>
                  </RecordingPulseOuter>
                  <RecordingText>
                    <RecordingTime>
                      {formatRecordingTime(recordingDuration)}
                    </RecordingTime>
                    <RecordingSubtext>
                      Recording... (max 60s)
                    </RecordingSubtext>
                  </RecordingText>
                  <RecordingActions>
                    <RecordingCancelButton
                      type="button"
                      onClick={cancelRecording}
                    >
                      Cancel
                    </RecordingCancelButton>
                    <RecordingStopButton
                      type="button"
                      onClick={stopRecording}
                    >
                      <HiStop color="var(--white)" />
                      Stop
                    </RecordingStopButton>
                  </RecordingActions>
                </RecordingContainer>
              ) : (
                <StartRecordButton
                  type="button"
                  onClick={startRecording}
                >
                  <StartRecordIcon>
                    <BsMic color="#4DDBBA" />
                  </StartRecordIcon>
                  <StartRecordText>
                    <StartRecordTitle>
                      Tap to Record
                    </StartRecordTitle>
                    <StartRecordSubtitle>
                      Up to 60 seconds
                    </StartRecordSubtitle>
                  </StartRecordText>
                </StartRecordButton>
              )}
            </FormGroup>
          )}
        </>
      )}
    </PropertiesBody>
    {selectedNode && (
      <PropertiesFooter>
        {confirmingDelete && (
          <DeleteConfirmTooltip $wide={deleteConfirmWide}>
            <p>{deleteConfirmMessage}</p>
            <div className="confirm-actions">
              <ConfirmBtn onClick={onCancelDelete}>Cancel</ConfirmBtn>
              <ConfirmBtn $danger onClick={onConfirmDelete}>
                Delete
              </ConfirmBtn>
            </div>
          </DeleteConfirmTooltip>
        )}

        <Button variant="danger" rounded onClick={() => onRequestDelete(selectedNode.id)}>
          Delete Node
        </Button>
        <Button
          rounded
          onClick={() => {
            if (onSaveStep && !onSaveStep()) return;
            setPropertiesPanelOpen(false);
            onCancelDelete();
          }}>
          Save Step
        </Button>
      </PropertiesFooter>
    )}
  </PropertiesPanel>
  );
};

export default FlowBuilderPropertiesPanel;
